import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Badge } from "react-bootstrap";
import { createPortal } from "react-dom";
import MaterialIcon, { MtIcon } from "../material-icon/material-icon";
import styles from "./app-drop-down.module.scss";

interface MenuItem {
  id?: string;
  text: string;
  onClick?: () => void;
  label?: string;
}

interface IProps {
  items: MenuItem[];
  buttonSize?: number;
  onItemClick?: (item: MenuItem) => void;
  icon?: MtIcon;
  iconColor?: string;
  id?: string;
  hasDropdownBg?: boolean;
  bgColor?: string;
}

const MENU_GAP = 6;
const VIEWPORT_PADDING = 8;

const AppDropDown = (props: IProps) => {
  const [showMenu, setShowMenu] = useState(false);
  const [isPositioned, setIsPositioned] = useState(false);
  const [menuCoords, setMenuCoords] = useState({ top: 0, left: 0 });
  const menuButtonRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const updateMenuPosition = useCallback(() => {
    const button = menuButtonRef.current;
    const menu = menuRef.current;
    if (!button || !menu) {
      return;
    }

    const buttonRect = button.getBoundingClientRect();
    const menuWidth = menu.offsetWidth || 160;
    const menuHeight = menu.offsetHeight || 80;
    const maxLeft = window.innerWidth - menuWidth - VIEWPORT_PADDING;
    const maxTop = window.innerHeight - menuHeight - VIEWPORT_PADDING;

    let left = buttonRect.right - menuWidth;
    if (left < VIEWPORT_PADDING) {
      left = Math.min(buttonRect.left, maxLeft);
    }
    left = Math.max(VIEWPORT_PADDING, Math.min(left, maxLeft));

    let top = buttonRect.bottom + MENU_GAP;
    if (
      top > maxTop &&
      buttonRect.top - MENU_GAP - menuHeight >= VIEWPORT_PADDING
    ) {
      top = buttonRect.top - MENU_GAP - menuHeight;
    }
    top = Math.max(VIEWPORT_PADDING, Math.min(top, maxTop));

    setMenuCoords({ top, left });
    setIsPositioned(true);
  }, []);

  const handleClickOutside = useCallback((e: MouseEvent) => {
    const target = e.target as Node;
    if (
      menuButtonRef.current?.contains(target) ||
      menuRef.current?.contains(target)
    ) {
      return;
    }
    setShowMenu(false);
  }, []);

  const onMenuShow = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu((open) => {
      if (open) {
        setIsPositioned(false);
      }
      return !open;
    });
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);

  useLayoutEffect(() => {
    if (!showMenu) {
      return;
    }

    updateMenuPosition();

    const onReposition = () => updateMenuPosition();
    window.addEventListener("resize", onReposition);
    document.addEventListener("scroll", onReposition, true);

    return () => {
      window.removeEventListener("resize", onReposition);
      document.removeEventListener("scroll", onReposition, true);
    };
  }, [showMenu, updateMenuPosition]);

  return (
    <div
      className={`${styles["drop-down-container"]} d-flex`}
      ref={menuButtonRef}
    >
      <div
        className={`${styles["drop-down-icon"]} ${
          props.hasDropdownBg !== false ? styles["with-bg"] : ""
        } ${
          props.icon === "more_horiz"
            ? `px-3 ${styles.moreHorBorderRadius}`
            : ""
        }`}
        style={props.bgColor ? { backgroundColor: props.bgColor } : undefined}
        onClick={onMenuShow}
        id={props.id}
      >
        <MaterialIcon
          icon={props.icon ? `${props.icon}` : `more_vert`}
          color={props.iconColor || "#7483CC"}
          className="cursor-pointer"
          size={props.buttonSize || 18}
        />
      </div>
      {showMenu &&
        createPortal(
          <div
            className={`${styles["menu-container"]} ${styles["show-menu"]}`}
            ref={menuRef}
            style={{
              top: menuCoords.top,
              left: menuCoords.left,
              visibility: isPositioned ? "visible" : "hidden",
            }}
          >
            <ul>
              {props.items.map((item, index) => (
                <li
                  key={index}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowMenu(false);
                    if (props.onItemClick) props.onItemClick(item);
                    if (item.onClick) item.onClick();
                  }}
                >
                  <div className="d-flex align-items-center">
                    {item.text}
                    {item.label && (
                      <Badge className={`${styles.badge} ms-1`} bg="#">
                        {item.label}
                      </Badge>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default AppDropDown;
